import { UnitsPreferences } from "@src/core/api/unitsPreferences";
import { processNumericValue } from "@src/core/engine/helpers/processNumericValue";
import { PhysicalType } from "@src/core/engine/units/constants";
import { unitsRepository } from "@src/core/engine/units/unitsRepository";
import { utilsRepository } from "@src/core/engine/utils/utilsRepository";
import { getEmptyRow } from "@src/routes/projects/$projectId/selections/batch/$category/store/helpers/getEmptyRow";
import { isRowEmpty } from "@src/routes/projects/$projectId/selections/batch/$category/store/helpers/isRowEmpty";
import { defaultUnits, getUnit } from "@src/shared/hooks/useUnitConversion";
import { CellErrorValue, CellValue, Workbook } from "exceljs";
import { isBoolean, isNullish, isNumber, isObject, isString } from "radashi";
import { isBatchSelectionInputColumn } from "./isBatchSelectionInputColumn";

type ParsingErrorReason = "templateError";

class ParseXlsxDataError extends Error {
  reason: ParsingErrorReason;

  constructor(message: string, reason: ParsingErrorReason) {
    super(message);
    this.name = "ParseXlsxDataError";
    this.reason = reason;
  }
}

export function isParseXlsxDataError(
  error: unknown,
): error is ParseXlsxDataError {
  return error instanceof ParseXlsxDataError;
}

export function parseXlsxData(workbook: Workbook, units: UnitsPreferences) {
  const firstSheet = workbook.worksheets[0];
  const schemaRow = firstSheet.getRow(1);
  const headerRow = firstSheet.getRow(3);

  const columnsMapping: Record<number, string> = {};
  schemaRow.eachCell((cell, colNumber) => {
    if (!isString(cell.value)) return;
    columnsMapping[colNumber] = cell.value;
  });

  if (!Object.keys(columnsMapping).length) {
    throw new ParseXlsxDataError(
      "No columns mapping found in the Excel sheet.",
      "templateError",
    );
  }

  const dataRows = firstSheet.getRows(4, firstSheet.rowCount - 3) || [];
  const parsedData = dataRows
    .map((row) => {
      const rowData = getEmptyRow();
      row.eachCell((cell, colNumber) => {
        const columnName = columnsMapping[colNumber];
        const value = parseCellValue(cell.value);
        if (!isBatchSelectionInputColumn(columnName) || isNullish(value))
          return;

        // Some values have rounding issues when imported from Excel, so we
        // process them to ensure they are rounded correctly
        const processed = processNumericValue(
          value,
          { maxDecimal: 3, physicalType: getColumnPhysicalType(columnName) },
          {
            conversionFn(value, physicalType) {
              const unitInTemplate = headerRow.getCell(colNumber).value;
              const options = unitsRepository.getOptions();
              const unitsKey = getUnitsKey(columnName);
              if (!unitsKey || !physicalType) return value;
              const unitFrom = options[unitsKey].options.find(
                (option) => option.label === unitInTemplate,
              )?.id;
              const unitTo = getUnit(physicalType, units, defaultUnits);
              if (!unitFrom || !unitTo) return value;
              return utilsRepository.convertUnit(value, unitFrom, unitTo);
            },
            round: true,
          },
        );
        rowData[columnName] = `${processed}`.trim();
      });
      return rowData;
    })
    .filter((row) => !isRowEmpty(row));

  return parsedData;
}

function getColumnPhysicalType(column: string): PhysicalType | null {
  switch (column) {
    case "differentialTemperature":
      return "PHYS_DIFFTEMP";
    case "dp":
      return "PHYS_DIFFPRESS";
    case "flow":
      return "PHYS_FLOW";
    case "power":
      return "PHYS_TH_POWER";
    default:
      return null;
  }
}

function getUnitsKey(column: string): keyof UnitsPreferences | null {
  switch (column) {
    case "differentialTemperature":
      return "temperatureDifference";
    case "dp":
      return "differentialPressure";
    case "flow":
      return "flow";
    case "power":
      return "thermalPower";
    default:
      return null;
  }
}

function parseCellValue(value: CellValue): number | string | null {
  if (isObject(value)) {
    if ("formula" in value) return parseValue(value.result);
    if ("sharedFormula" in value) return parseValue(value.result);
    if ("text" in value) return value.text;
    if ("richText" in value)
      return value.richText.map((item) => item.text).join(" ");
  }

  return parseValue(value);
}

function parseValue(
  value: boolean | CellErrorValue | Date | number | string | null | undefined,
): number | string | null {
  if (isNumber(value) || isString(value)) return value;
  if (isBoolean(value)) return value ? "true" : "false";
  if (value instanceof Date) return value.toISOString();
  if (isNullish(value)) return null;
  if ("error" in value) return null;
  return null;
}
